/**
 * ConsoleGate — Tiny gate component that wires the debug flag to capture
 * install/uninstall lifecycle and conditionally mounts <InAppConsole />.
 *
 * Design: in-app-console design.md — "The install/uninstall reaction lives in a
 * tiny gate component (per design), NOT in module scope."
 *
 * Behavior:
 *   - flag ON  → consoleCapture.install() + render <InAppConsole />
 *   - flag OFF → effect cleanup calls consoleCapture.uninstall() + render null
 *   - Unmount   → effect cleanup calls consoleCapture.uninstall() (IAC-2 teardown)
 *
 * consoleCapture.install() and consoleCapture.uninstall() are idempotent (IAC-3),
 * so extra calls when already in the desired state are safe.
 *
 * Mounted in main.tsx beside <DeviceEnrollBanner />, ABOVE <RouterProvider />.
 */
import { useEffect } from 'react';
import { useDebugFlag } from './useDebugFlag';
import { consoleCapture } from './consoleCapture';
import { InAppConsole } from '@/shared/ui/devtools/InAppConsole';

export function ConsoleGate() {
  const enabled = useDebugFlag();

  useEffect(() => {
    if (!enabled) {
      // Ensure teardown when flag is OFF (handles the OFF→OFF idempotency case via IAC-3).
      consoleCapture.uninstall();
      return;
    }
    consoleCapture.install();
    // Cleanup: uninstall when flag turns OFF or component unmounts (IAC-2).
    return () => {
      consoleCapture.uninstall();
    };
  }, [enabled]);

  if (!enabled) return null;
  return <InAppConsole />;
}
